import { useIntl } from 'react-intl';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowCircleRightSharpIcon from '@mui/icons-material/ArrowCircleRightSharp';

import TextFieldBase from '@/components/mui/Form/TextField/TextFieldBase';

interface TagsSearchInputProps {
  params: AutocompleteRenderInputParams;
  disabled: boolean;
  isBusy?: boolean;
  onAdd: () => void;
  autoFocus?: boolean;
  label?: string;
}

// renderInput target for the tag-manage dialogs: TextFieldBase with the "add" arrow button in
// the end adornment (commits the current input via onAdd; freeSolo Enter/select is handled by
// the Autocomplete's own onChange). Shared by the three tag dialogs.
const TagsSearchInput = ({ params, disabled, isBusy = false, onAdd, autoFocus = true, label }: TagsSearchInputProps) => {
  const { formatMessage } = useIntl();
  return (
    <TextFieldBase
      label={label ?? formatMessage({ id: 'modal.document.tags.inputLabel', defaultMessage: 'Tag Name' })}
      autoFocus={autoFocus}
      fullWidth
      // MUI Autocomplete puts `padding: 9` on the input ROOT (and trims the <input> padding to
      // 7.5px to compensate). That root padding wraps the flex children, so the tall arrow button
      // gets +18px stacked on its own height → the field inflates. Fix: move the vertical padding
      // OFF the root (button no longer inflated) and back ONTO the input at the normal medium value
      // (field keeps its 56px height and the outlined label stays vertically centered). The
      // descendant selector out-specifies MUI's own `.MuiAutocomplete-root … .MuiAutocomplete-input`.
      sx={{
        mt: 1,
        '& .MuiOutlinedInput-root': { paddingBlock: 0 },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': { paddingBlock: '16.5px' },
      }}
      slotProps={{
        htmlInput: params.inputProps,
        // Padding reserve is already off (the Autocomplete sets disableClearable + forcePopupIcon={false},
        // so its root never gets hasClearIcon/hasPopupIcon); vertical padding is handled by the sx above.
        // Spread all of params.InputProps (restores ref + onMouseDown/focus wiring) and override only
        // the end adornment.
        input: {
          ...params.InputProps,
          endAdornment: (
            <IconButton
              aria-label="enter"
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                onAdd();
              }}
              sx={{ rotate: '-90deg' }}
            >
              {isBusy ? (
                <CircularProgress size={24} />
              ) : (
                <ArrowCircleRightSharpIcon color={disabled ? 'disabled' : 'primary'} sx={{ width: 32, height: 32 }} />
              )}
            </IconButton>
          ),
        },
      }}
    />
  );
};

export default TagsSearchInput;
